import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContractInsightsData } from '@/pages/dashboard/ContractInsights';
import { vehicleRepaid } from '@/components/contracts/insights/vehicleRepaid';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { Badge } from '@/components/ui/badge';
import { Search, Check, AlertTriangle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { businessDay, businessToday, formatBusinessTime, parseBusinessDay } from '@/lib/format';
import { format, startOfWeek, addWeeks, addDays, differenceInCalendarDays, isBefore, isAfter, isSameWeek, isMonday, isSameDay } from 'date-fns';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';

type ViewMode = 'all' | 'partner' | 'management';

interface Props {
    data: ContractInsightsData;
}

const PAGE_SIZE = 15;

interface RepaymentRecord {
    id: number;
    contract_id: number;
    amount: number;
    description: string | null;
    transaction_at: string;
    driver_name: string;
    note: string | null;
    total_count: number;
}

interface PaymentWeek {
    weekStart: Date;
    weekEnd: Date;
    expected: number;
    contractExpected: number;
    payments: RepaymentRecord[];
    totalPaid: number;
    status: 'paid' | 'delayed' | 'partial' | 'missed' | 'upcoming' | 'pending';
    delayDays: number | null;
    note: string | null;
    contractAllocation: number;
}


const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

export default function ContractInsightVehicle({ data }: Props) {
    const [activeView, setActiveView] = useState<ViewMode>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
    const debouncedSearch = useDebounce(searchTerm, 300);

    const toggleWeekExpanded = (weekKey: string) => {
        setExpandedWeeks(prev => {
            const next = new Set(prev);
            if (next.has(weekKey)) {
                next.delete(weekKey);
            } else {
                next.add(weekKey);
            }
            return next;
        });
    };

    const contractId = data.contract?.id;

    // Reset to page 1 when search changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

    // Fetch ALL repayments for this contract (no pagination - we need all for schedule generation)
    const { data: allRepayments, isLoading } = useQuery({
        queryKey: ['contract-all-repayments', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase.rpc('get_contract_repayments', {
                p_contract_id: contractId,
                p_from_date: null,
                p_to_date: null,
                p_search: null,
                p_page: 1,
                p_page_size: 1000  // Get all repayments
            });

            if (error) throw error;
            return (data || []) as RepaymentRecord[];
        },
        enabled: !!contractId,
    });

    // Generate payment schedule with expected weeks - Vehicle tab focuses only on contract instalments
    const paymentSchedule = useMemo(() => {
        if (!data.instalments?.length || !allRepayments) return [];

        // Sort instalments by starts_at (ascending).
        //
        // `starts_at` is a DATE column — the Ghana calendar day, serialised 'yyyy-MM-dd' — and the
        // weeks below are built with date-fns, which reads a Date's *local* parts. So the day has to
        // enter as a calendar-day Date: `new Date('2026-06-01')` is UTC midnight, i.e. May 31 west of
        // Greenwich, and `startOfWeek` on it returned the *previous* Monday (probed New York and
        // Santiago: 2026-05-25 for a 2026-06-01 contract). Every week label, the Expected/Paid pairing
        // and the `expectedByMonday` lookup below shifted a week for those viewers — 287 of 437
        // production instalments start on a Monday, which is exactly the case that breaks.
        const sortedInstalments = [...data.instalments]
            .map(i => ({ amount: i.amount, startsAt: parseBusinessDay(i.starts_at)! }))
            .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());

        // Get contract start date from first instalment
        const contractStart = sortedInstalments[0].startsAt;
        // The Ghana day, not the viewer's: at 01:00 Accra on a Monday a New York viewer's `new Date()`
        // is still Sunday, which flipped the current week to 'upcoming' and suppressed 'pending'.
        const today = businessToday();

        // The Ghana day of the last payment. `transaction_at`/`occurred_at` are instants; the loop
        // below compares them against calendar-day Mondays, so they enter as the Ghana day they
        // belong to. A final payment at 01:00 Accra on a Monday read as an instant is the Sunday
        // before in New York, and the loop then stopped one week short — dropping the very week
        // that payment is in.
        const lastPaymentDay = allRepayments.length > 0
            ? allRepayments.reduce((latest, r) => {
                const paymentDate = businessDay(r.transaction_at)!;
                return paymentDate > latest ? paymentDate : latest;
            }, businessDay(allRepayments[0].transaction_at)!)
            : null;

        // Determine the schedule end date based on contract status
        // For COMPLETED contracts, stop at the last payment date
        // For TERMINATED contracts, stop at the termination date
        // For active contracts, continue to today
        let scheduleEnd = today;
        if (data.current_status === 'COMPLETED' && lastPaymentDay) {
            scheduleEnd = lastPaymentDay;
        } else if (data.current_status === 'TERMINATED') {
            const terminatedUpdate = data.status_history?.find(s => s.status === 'TERMINATED');
            if (terminatedUpdate?.occurred_at) {
                scheduleEnd = businessDay(terminatedUpdate.occurred_at)!;
            }
            // A terminated contract's schedule ends where the overdue engine's does: at the last
            // payment. 26 of 96 terminated contracts were terminated AFTER their final payment, and
            // the Mondays in between have no priced week to read an Expected from — they would fall
            // back to the bare contract instalment and quietly drop every loan contribution.
            if (lastPaymentDay && lastPaymentDay < scheduleEnd) scheduleEnd = lastPaymentDay;
        }

        // Generate all expected weeks from contract start to schedule end
        const weeks: PaymentWeek[] = [];
        let currentWeekStart = startOfWeek(contractStart, { weekStartsOn: 1 }); // Monday
        let instalmentIndex = 0;

        while (isBefore(currentWeekStart, scheduleEnd) || isSameWeek(currentWeekStart, scheduleEnd, { weekStartsOn: 1 })) {
            // Week ends on Sunday (6 days after Monday start)
            const weekEnd = addDays(currentWeekStart, 6);
            // Use next Monday as exclusive boundary for filtering payments
            const nextWeekStart = addWeeks(currentWeekStart, 1);

            // Find applicable contract instalment rate for this week
            while (
                instalmentIndex < sortedInstalments.length - 1 &&
                sortedInstalments[instalmentIndex + 1].startsAt <= currentWeekStart
            ) {
                instalmentIndex++;
            }

            const contractInstalment = sortedInstalments[instalmentIndex]?.amount || 0;

            // For Vehicle tab, expected is only the contract instalment (no loans)
            const expectedAmount = contractInstalment;

            // Find payments that fall within this week (Monday to Sunday inclusive).
            // Bucket by the Ghana calendar day — the same day `get_contract_repayments` filters on
            // (`transaction_at::date`) — not by the raw instant: 560 production payments land on a
            // Monday before 04:00 UTC and every one of them fell into the *previous* week for a
            // viewer west of Greenwich, moving both the Paid figure and the week's status.
            const weekPayments = allRepayments.filter(r => {
                const paymentDay = businessDay(r.transaction_at)!;
                return paymentDay >= currentWeekStart && paymentDay < nextWeekStart;
            });

            const totalPaid = weekPayments.reduce((sum, p) => sum + p.amount, 0);

            // Calculate contract allocation from the payment
            const totalAllocated = (data.allocation_summary?.contract || 0) +
                (data.allocation_summary?.loans || 0) +
                (data.allocation_summary?.insurance_loans || 0);

            let contractAlloc = totalPaid;
            if (totalAllocated > 0 && totalPaid > 0) {
                const contractRatio = (data.allocation_summary?.contract || 0) / totalAllocated;
                contractAlloc = totalPaid * contractRatio;
            }

            // Determine status based on total paid vs expected
            // Status should be based on what the driver paid, not how it was allocated
            const isUpcoming = isAfter(currentWeekStart, today);
            const isCurrentWeek = isSameWeek(currentWeekStart, today, { weekStartsOn: 1 });
            const isTodayMonday = isMonday(today);
            let status: PaymentWeek['status'] = 'missed';

            if (isUpcoming) {
                status = 'upcoming';
            } else if (totalPaid >= expectedAmount) {
                // Check if payment was made on Monday (first day of the week)
                const weekMonday = currentWeekStart;
                const firstPayment = weekPayments.length > 0
                    ? weekPayments.reduce((earliest, p) =>
                        new Date(p.transaction_at) < new Date(earliest.transaction_at) ? p : earliest
                    ) : null;
                // Ghana day again: the reduce picks the earliest *instant* (order-invariant), but the
                // day it is compared against the Monday on must be the business one.
                const firstPaymentDate = firstPayment ? businessDay(firstPayment.transaction_at) : null;
                const paidOnMonday = firstPaymentDate && isSameDay(firstPaymentDate, weekMonday);
                status = paidOnMonday ? 'paid' : 'delayed';
            } else if (totalPaid > 0) {
                status = 'partial';
            } else if (isCurrentWeek && isTodayMonday) {
                status = 'pending';
            }

            // Calculate delay days if delayed
            let delayDays: number | null = null;
            if (status === 'delayed' && weekPayments.length > 0) {
                const weekMonday = currentWeekStart;
                const firstPayment = weekPayments.reduce((earliest, p) =>
                    new Date(p.transaction_at) < new Date(earliest.transaction_at) ? p : earliest
                );
                const firstPaymentDate = businessDay(firstPayment.transaction_at)!;
                // Whole calendar days between two calendar-day Dates. Subtracting milliseconds would
                // reintroduce the same class of bug from the other side — a DST week is 23 or 25 hours.
                delayDays = differenceInCalendarDays(firstPaymentDate, weekMonday);
            }

            // Check for notes covering this week
            // `period_start`/`period_end` are DATE columns, so they read as calendar days too. As
            // instants they are UTC midnight, which is *ahead* of a local-midnight Monday anywhere east
            // of Greenwich — a Mon–Sun note then matched no week at all and vanished from the table
            // (probed London in BST and Kiritimati; 41 production notes start on a Monday).
            const applicableNote = data.repayment_notes?.find(note => {
                const noteStart = parseBusinessDay(note.period_start)!;
                const noteEnd = parseBusinessDay(note.period_end)!;
                return currentWeekStart >= noteStart && currentWeekStart <= noteEnd;
            });

            weeks.push({
                weekStart: new Date(currentWeekStart),
                weekEnd: new Date(weekEnd),
                expected: expectedAmount,
                contractExpected: expectedAmount,
                payments: weekPayments,
                totalPaid,
                status,
                delayDays,
                note: applicableNote?.reason || null,
                contractAllocation: contractAlloc,
            });

            currentWeekStart = addWeeks(currentWeekStart, 1);
        }

        // Trim leading missed weeks before the first payment (chronological order)
        const firstPaymentIdx = weeks.findIndex(w => w.totalPaid > 0);
        const trimmed = firstPaymentIdx > 0 ? weeks.slice(firstPaymentIdx) : weeks;

        // Sort by most recent first
        return trimmed.reverse();
    }, [data.instalments, data.repayment_notes, data.allocation_summary, data.current_status, data.status_history, allRepayments]);

    // The Expected the overdue engine priced each week at — contract instalment plus whatever loan
    // instalments were active — keyed by the week's Monday as 'yyyy-MM-dd'. `week.weekStart` is a
    // calendar-day Date, so `format` reads its local parts back out as the same Ghana day the
    // server keyed on.
    const expectedByMonday = useMemo(() => {
        const byMonday = new Map<string, number>();
        for (const week of data.overdue?.weeks ?? []) byMonday.set(week.monday, week.expected);
        return byMonday;
    }, [data.overdue]);

    const paymentScheduleWithInterest = useMemo(() => paymentSchedule.map(week => {
        // A week the engine never priced falls back to the bare contract instalment, which makes
        // the ratio 1 — the same answer the old client-side lookup gave outside every loan period.
        const mergedExpected = expectedByMonday.get(format(week.weekStart, 'yyyy-MM-dd')) ?? week.contractExpected;

        // Compute per-week contract allocation using the ratio of contract instalment
        // to merged instalment (which includes loans/insurance).
        // This is more accurate than the global ratio because it accounts for
        // which loans were active in each specific week.
        const contractRatio = mergedExpected > 0
            ? week.contractExpected / mergedExpected
            : 1;

        return {
            ...week,
            expected: mergedExpected,  // Override with merged amount
            contractAllocation: week.totalPaid * contractRatio,  // Override with per-week ratio
        };
    }), [paymentSchedule, expectedByMonday]);

    // Filter by search term
    const filteredSchedule = useMemo(() => {
        if (!debouncedSearch) return paymentScheduleWithInterest;

        const searchLower = debouncedSearch.toLowerCase();
        return paymentScheduleWithInterest.filter(week =>
            week.payments.some(p =>
                p.description?.toLowerCase().includes(searchLower) ||
                p.driver_name?.toLowerCase().includes(searchLower)
            ) ||
            week.note?.toLowerCase().includes(searchLower)
        );
    }, [paymentScheduleWithInterest, debouncedSearch]);

    // Pagination
    const totalCount = filteredSchedule.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const paginatedSchedule = filteredSchedule.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    // Stats for Vehicle tab
    const vehiclePrice = data.contract?.sold_at || 0;  // Total Repayable = Vehicle selling price
    const contractAllocationTotal = data.allocation_summary?.contract || 0;  // what the weekly payments put on the vehicle
    // On this tab a 0% rate is treated the same as NULL — no split.
    const rawManagementRate = data.contract?.management_rate ?? null;
    const managementRate = rawManagementRate !== null && rawManagementRate > 0 ? rawManagementRate : null;
    const hasMgmtSplit = managementRate !== null;

    // Reset to 'all' if management_rate is cleared
    const activeMode = hasMgmtSplit ? activeView : 'all';

    // Multiplier for current mode's portion of the allocation
    const modeMultiplier = activeMode === 'management' && managementRate !== null
        ? managementRate / 100
        : activeMode === 'partner' && managementRate !== null
            ? (100 - managementRate) / 100
            : 1;

    /**
     * THE THREE CARDS ABOVE THE TABLE, DERIVED ONCE — see `vehicleRepaid`.
     *
     * THE DEPOSIT COUNTS TOWARDS THE VEHICLE [D-B58]. The money engine treats the vehicle as paid
     * for when the deposit, net of refunds, plus the vehicle share of the payments reach the selling
     * price; counting it here is what lets Outstanding close at nothing on the day the engine says
     * the vehicle is paid for, rather than sitting one deposit short for ever. It is also what the
     * Overview tab has counted as paid all along, so the two tabs of one contract stop disagreeing.
     *
     * THE SHARE VIEWS ARE UNMOVED, AND THE HELPER SAYS WHY: they state a payout and a management
     * fee, both of which are computed off allocations alone.
     *
     * THE WEEKLY TABLE BELOW AND THE AVERAGE PAYMENT CARD STAY WHERE THEY ARE, because a deposit is
     * not a weekly payment — it belongs to no Monday and would have to be smeared across the
     * schedule to appear there. The card is the authority on what has been paid towards the vehicle;
     * the table is the authority on what arrived in a given week.
     */
    const vehicleTotals = vehicleRepaid({
        price: vehiclePrice,
        contractAllocation: contractAllocationTotal,
        netDeposit: data.deposit?.amount ?? null,
        mode: activeMode,
        modeMultiplier,
    });

    // Latest contract instalment. `new Date` on a DATE string is UTC midnight rather than the calendar
    // day, but this only ever orders instalments against each other — the same shift applies to both
    // sides, so the winning row is the same in every viewer zone (probed) and no day is ever displayed.
    const latestInstalment = data.instalments && data.instalments.length > 0
        ? [...data.instalments].sort((a, b) =>
            new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime()
        )[0]?.amount || 0
        : 0;

    // Average of contract allocations per week (missed weeks count as 0)
    // Multiple payments in a week are already summed in contractAllocation
    const totalWeeks = paymentSchedule.length;
    const totalContractAllocations = paymentSchedule.reduce((sum, w) => sum + w.contractAllocation, 0);
    const avgContractPayment = totalWeeks > 0 ? (totalContractAllocations * modeMultiplier) / totalWeeks : 0;

    // Card labels based on mode. Each description says what its own card counts, and the three All
    // descriptions name the deposit because that is what moved [D-B58].
    const repaidLabel = activeMode === 'management' ? 'Retained' : activeMode === 'partner' ? 'Payout' : 'Repaid';
    const repaidDescription = activeMode === 'management'
        ? `Total management fee retained (${managementRate}% of vehicle allocation).`
        : activeMode === 'partner'
            ? `Total partner payout portion (${managementRate !== null ? 100 - managementRate : 100}% of vehicle allocation).`
            : 'The deposit, net of any refund, plus everything the payments have put towards the vehicle.';
    const totalRepayableDescription = activeMode === 'partner'
        ? `Partner's portion of the vehicle selling price.`
        : activeMode === 'management'
            ? `Management's portion of the vehicle selling price.`
            : 'The vehicle selling price, which the deposit and the weekly instalments pay off between them.';
    const outstandingDescription = activeMode === 'partner'
        ? `What is left of the partner's portion of the selling price.`
        : activeMode === 'management'
            ? `What is left of management's portion of the selling price.`
            : 'The selling price less the deposit and everything the payments have put towards the vehicle.';




    const getStatusBadge = (status: PaymentWeek['status'], delayDays?: number | null) => {
        switch (status) {
            case 'paid':
                return (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        Paid
                    </Badge>
                );
            case 'partial':
                return (
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Partial
                    </Badge>
                );
            case 'missed':
                return (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Missed
                    </Badge>
                );
            case 'delayed':
                return (
                    <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Delayed {delayDays ? `(${delayDays}d)` : ''}
                    </Badge>
                );
            case 'upcoming':
                return (
                    <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
                        Upcoming
                    </Badge>
                );
            case 'pending':
                return (
                    <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                        Pending
                    </Badge>
                );
        }
    };

    return (
        <div className="space-y-6">
            {/* Segmented Toggle — only when management_rate is set */}
            {hasMgmtSplit && (
                <div className="flex items-center gap-1 bg-muted p-1 rounded-lg w-fit">
                    {(['all', 'partner', 'management'] as const).map((view) => (
                        <button
                            key={view}
                            onClick={() => setActiveView(view)}
                            className={cn(
                                'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                                activeMode === view
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {view === 'all' ? 'All' : view === 'management' ? 'Management' : 'Partner'}
                        </button>
                    ))}
                </div>
            )}

            {/* Payment Summary Cards */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Total Repayable"
                    value={<CurrencyDisplay amount={vehicleTotals.price} />}
                    description={totalRepayableDescription}
                />
                <StatCard
                    title={repaidLabel}
                    value={<CurrencyDisplay amount={vehicleTotals.repaid} />}
                    description={repaidDescription}
                />
                <StatCard
                    title="Outstanding"
                    value={<CurrencyDisplay amount={vehicleTotals.outstanding} />}
                    description={outstandingDescription}
                />
                <StatCard
                    title="Instalment"
                    value={<CurrencyDisplay amount={latestInstalment * modeMultiplier} />}
                    description={activeMode === 'all' ? 'The current weekly instalment amount expected for vehicle repayment.' : `Weekly ${activeMode} portion of the instalment.`}
                />
                <StatCard
                    title="Average Payment"
                    value={<CurrencyDisplay amount={avgContractPayment} />}
                    description={`Average weekly ${activeMode} allocation across ${totalWeeks} weeks. Missed weeks count as zero; multiple payments in a week are summed together. A deposit is not a weekly payment, so it is not counted here.`}
                />
            </div>

            {/* Payment Schedule Table */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by description..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {totalPages > 0 && (
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                        />
                    )}
                </div>

                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : paginatedSchedule.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Week</TableHead>
                                <TableHead>Expected</TableHead>
                                <TableHead>Paid</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-[200px]">Note</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedSchedule.map((week) => {
                                const weekKey = week.weekStart.toISOString();
                                const isExpanded = expandedWeeks.has(weekKey);
                                // Make expandable if there are multiple actual payments in the week
                                const hasMultiplePayments = week.payments.length > 1;

                                return (
                                    <Fragment key={weekKey}>
                                        <TableRow
                                            className={cn(hasMultiplePayments && "cursor-pointer hover:bg-muted/50")}
                                            onClick={() => hasMultiplePayments && toggleWeekExpanded(weekKey)}
                                        >
                                            <TableCell className="text-muted-foreground whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {hasMultiplePayments && (
                                                        isExpanded
                                                            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                            : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                    {format(week.weekStart, 'MMM d')} - {format(week.weekEnd, 'MMM d, yyyy')}
                                                    {hasMultiplePayments && (
                                                        <span className="text-xs text-muted-foreground">({week.payments.length})</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {formatCurrency(week.contractExpected * modeMultiplier)}
                                            </TableCell>
                                            <TableCell className={cn(
                                                "font-medium",
                                                week.status === 'paid' && "text-green-600",
                                                week.status === 'partial' && "text-yellow-600",
                                                week.status === 'missed' && "text-red-600"
                                            )}>
                                                {formatCurrency(week.contractAllocation * modeMultiplier)}
                                            </TableCell>
                                            <TableCell>
                                                {getStatusBadge(week.status, week.delayDays)}
                                            </TableCell>
                                            <TableCell className="max-w-[150px] truncate text-muted-foreground">
                                                {week.note || '-'}
                                            </TableCell>
                                        </TableRow>
                                        {/* Expanded rows showing actual payments */}
                                        {hasMultiplePayments && isExpanded && week.payments.map((payment) => {
                                            // Use the same per-week ratio as the summary row
                                            const weekContractRatio = week.totalPaid > 0 ? week.contractAllocation / week.totalPaid : 1;
                                            const paymentContractAlloc = payment.amount * weekContractRatio;
                                            return (
                                                <TableRow key={payment.id} className="bg-muted/30 border-0">
                                                    <TableCell className="pl-10 text-sm text-muted-foreground">
                                                        ↳ {`${format(businessDay(payment.transaction_at)!, 'MMM d')}, ${formatBusinessTime(payment.transaction_at)}`}
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell className="text-sm text-muted-foreground">
                                                        {formatCurrency(paymentContractAlloc * modeMultiplier)}
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                                        {payment.description || '-'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">
                        {searchTerm ? 'No weeks match your search' : 'No payment schedule available'}
                    </p>
                )}
            </div>
        </div>
    );
}
